import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { UserPlus, UserCheck, UserX, Clock, LogIn, LogOut as LogOutIcon, Activity, Users, ArrowLeft, Trash2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

function UserManagement() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [testUsers, setTestUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [cleaningInProgress, setCleaningInProgress] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const [allRes, pendingRes, testRes] = await Promise.all([
        axios.get(`${API}/users/all`),
        axios.get(`${API}/users/pending`),
        axios.get(`${API}/admin/test-users`).catch(() => ({ data: [] }))
      ]);
      setAllUsers(allRes.data);
      setPendingUsers(pendingRes.data);
      setTestUsers(testRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const cleanupTestUsers = async () => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${testUsers.length} compte(s) de test ?\n\nCette action est irréversible.`)) {
      return;
    }
    setCleaningInProgress(true);
    try {
      const res = await axios.delete(`${API}/admin/cleanup-test-users`);
      toast.success(`${res.data.deleted_users} utilisateur(s) supprimé(s)`);
      fetchUsers();
    } catch (e) {
      toast.error('Erreur lors du nettoyage');
    } finally {
      setCleaningInProgress(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`);
      toast.success('Utilisateur approuvé');
      fetchUsers();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/reject`);
      toast.success('Utilisateur rejeté');
      fetchUsers();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const viewActivity = async (userId) => {
    try {
      const res = await axios.get(`${API}/users/${userId}/activity`);
      setSelectedUser(res.data.user);
      setUserActivity(res.data.login_history);
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800',
      secretary: 'bg-blue-100 text-blue-800',
      technician: 'bg-green-100 text-green-800'
    };
    const labels = {
      admin: 'Admin',
      secretary: 'Secrétaire',
      technician: 'Technicien'
    };
    return <Badge className={styles[role] || 'bg-gray-100'}>{labels[role] || role}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-amber-100 text-amber-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      active: 'Actif',
      pending: 'En attente',
      rejected: 'Rejeté'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => nav('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <img src="/kardif-eye.png" alt="KARDIF" className="h-9 w-9 object-contain" />
          </div>
          <h1 className="text-xl font-bold">Gestion des utilisateurs</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs defaultValue="pending">
          <TabsList className="mb-6">
            <TabsTrigger value="pending" className="data-[state=active]:bg-[#8B7355] data-[state=active]:text-white">
              <UserPlus className="h-4 w-4 mr-2" />
              En attente ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="data-[state=active]:bg-[#8B7355] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Tous les utilisateurs
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-[#8B7355] data-[state=active]:text-white">
              <Activity className="h-4 w-4 mr-2" />
              Activité
            </TabsTrigger>
            <TabsTrigger value="cleanup" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              <Trash2 className="h-4 w-4 mr-2" />
              Nettoyage ({testUsers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : pendingUsers.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <UserCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-[#6B7280]">Aucune demande en attente</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map(u => (
                  <Card key={u.id} className="border-amber-200 bg-amber-50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{u.name}</h3>
                          <p className="text-[#6B7280]">{u.email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            {getRoleBadge(u.role)}
                            <span className="text-sm text-[#6B7280]">
                              Inscrit le {formatDateTime(u.created_at).split(' ')[0]}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => approveUser(u.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Approuver
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => rejectUser(u.id)}
                          >
                            <UserX className="h-4 w-4 mr-2" />
                            Rejeter
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all">
            <div className="grid gap-4">
              {allUsers.filter(u => u.status !== 'pending').map(u => (
                <Card key={u.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#8B7355] flex items-center justify-center text-white font-semibold">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-medium">{u.name}</h3>
                          <p className="text-sm text-[#6B7280]">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getRoleBadge(u.role)}
                        {getStatusBadge(u.status)}
                        <div className="text-right text-sm text-[#6B7280]">
                          <p>Dernière connexion</p>
                          <p className="font-medium">{formatDateTime(u.last_login)}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => viewActivity(u.id)}>
                          <Activity className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            {selectedUser ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-[#8B7355]" />
                    Activité de {selectedUser.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[#6B7280]">Dernière connexion</p>
                        <p className="font-medium">{formatDateTime(selectedUser.last_login)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">Dernière déconnexion</p>
                        <p className="font-medium">{formatDateTime(selectedUser.last_logout)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <h4 className="font-medium mb-3">Historique des connexions</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {userActivity?.length > 0 ? (
                      userActivity.slice().reverse().map((entry, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                          {entry.type === 'login' ? (
                            <LogIn className="h-4 w-4 text-green-600" />
                          ) : (
                            <LogOutIcon className="h-4 w-4 text-red-600" />
                          )}
                          <span className={entry.type === 'login' ? 'text-green-700' : 'text-red-700'}>
                            {entry.type === 'login' ? 'Connexion' : 'Déconnexion'}
                          </span>
                          <span className="text-[#6B7280] text-sm">
                            {formatDateTime(entry.timestamp)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-[#6B7280]">Aucun historique disponible</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="text-center py-12">
                <CardContent>
                  <Activity className="h-12 w-12 text-[#D4C5A9] mx-auto mb-4" />
                  <p className="text-[#6B7280]">Sélectionnez un utilisateur dans l'onglet "Tous les utilisateurs" pour voir son activité</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="cleanup">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                  Nettoyage des comptes de test
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] mb-4">
                  Cette fonctionnalité permet de supprimer tous les comptes contenant "test", "robot" ou créés automatiquement pendant le développement.
                  <br />
                  <strong className="text-red-600">Attention : cette action est irréversible !</strong>
                </p>

                {testUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <UserCheck className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-green-700 font-medium">Aucun compte de test détecté</p>
                    <p className="text-[#6B7280] text-sm">Votre base de données est propre.</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-6">
                      <h4 className="font-medium mb-3">Comptes détectés ({testUsers.length}) :</h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3 bg-red-50">
                        {testUsers.map(u => (
                          <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded border border-red-200">
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-[#6B7280]">{u.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {getRoleBadge(u.role)}
                              {getStatusBadge(u.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={cleanupTestUsers}
                      disabled={cleaningInProgress}
                    >
                      {cleaningInProgress ? (
                        <>
                          <div className="spinner mr-2" />
                          Suppression en cours...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer {testUsers.length} compte(s) de test
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default UserManagement;
